import { Link } from "wouter";
import { SimpleHeader, SimpleFooter, AnimatedBlock } from "@/components/simple-page-chrome";
import type { BlogPostMeta } from "@/content/blog";

function formatDate(iso: string): string {
  const [year, month, day] = iso.split("-").map(Number);
  const date = new Date(Date.UTC(year, month - 1, day));
  return date.toLocaleDateString("es-MX", { day: "numeric", month: "long", year: "numeric", timeZone: "UTC" });
}

export function BlogPostLayout({
  post,
  children,
}: {
  post: BlogPostMeta;
  children: React.ReactNode;
}) {
  const blogPostingJsonLd = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: post.title,
    description: post.excerpt,
    datePublished: post.publishedAt,
    author: {
      "@type": "Person",
      name: post.author.name,
      jobTitle: post.author.role,
    },
    publisher: {
      "@type": "Organization",
      name: "MAPA35",
      logo: {
        "@type": "ImageObject",
        url: "https://www.mapa35.com/favicon.png",
      },
    },
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": `https://www.mapa35.com/blog/${post.slug}/`,
    },
  };

  return (
    <div className="min-h-screen bg-background">
      <SimpleHeader />
      <article className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-20">
        <AnimatedBlock>
          <Link href="/blog" className="text-sm text-primary hover:underline" data-testid="link-back-blog">
            ← Blog
          </Link>
          <h1 className="font-serif text-3xl sm:text-4xl font-bold text-foreground mt-4 mb-4">
            {post.title}
          </h1>
          <div className="flex items-center gap-3 text-sm text-muted-foreground mb-12">
            <span data-testid="text-post-author">Por {post.author.name}, {post.author.role}</span>
            <span aria-hidden="true">·</span>
            <time dateTime={post.publishedAt} data-testid="text-post-date">{formatDate(post.publishedAt)}</time>
          </div>
        </AnimatedBlock>

        <AnimatedBlock delay={0.05}>
          <div className="prose-blog max-w-none text-foreground [&_h2]:font-serif [&_h2]:text-2xl [&_h2]:font-bold [&_h2]:mt-12 [&_h2]:mb-4 [&_p]:text-muted-foreground [&_p]:leading-relaxed [&_p]:mb-5 [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:mb-5 [&_ul]:text-muted-foreground [&_li]:mb-2 [&_a]:text-primary [&_a]:hover:underline">
            {children}
          </div>
        </AnimatedBlock>

        <AnimatedBlock delay={0.1}>
          <div className="mt-16 pt-10 border-t">
            <p className="text-sm font-medium text-foreground mb-4">Sigue explorando</p>
            <div className="flex flex-wrap gap-4">
              <Link href="/faq" className="text-sm text-primary hover:underline" data-testid="link-post-to-faq">
                Preguntas frecuentes sobre la NOM-035 →
              </Link>
              <Link href="/glosario" className="text-sm text-primary hover:underline" data-testid="link-post-to-glosario">
                Glosario de términos NOM-035 →
              </Link>
              <Link href="/#contacto" className="text-sm text-primary hover:underline" data-testid="link-post-to-contacto">
                Solicitar diagnóstico gratuito →
              </Link>
            </div>
          </div>
        </AnimatedBlock>
      </article>
      <SimpleFooter />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(blogPostingJsonLd) }}
      />
    </div>
  );
}
